import { ChangeEvent, DragEvent, MouseEvent, useCallback, useEffect, useRef, useState } from "react";

import FileUploadMarkup from "./markup";
import { BaseProps, SerializedFile } from "./types";

export function FileUploader(props: BaseProps) {
	const {
		id,
		multiple,
		autoReplace,
		disabled,
		acceptFileTypes,
		errorText,
		label,
		uploadedFiles,
		removeUploadedFile,
		getFileFromServer,
	} = props;
	const fileInputRef = useRef<HTMLInputElement>();
	const processingUpdate = useRef(false);
	const [focused, setFocused] = useState(false);
	const [files, setFiles] = useState<SerializedFile[]>([]);

	const initialized = useRef(false);
	useEffect(() => {
		if (!initialized.current) return;
		const { id } = props;
		if (processingUpdate.current) {
			processingUpdate.current = false;
			return;
		}
		filesAdded(id, files);
	}, [files]);

	useEffect(() => {
		if (!initialized.current) return;
		processingUpdate.current = true;
		setFiles(props.files || []);
	}, [props.files]);

	useEffect(() => {
		//MSC (5/20/2026)If app loads too quickly, the [files] useEffect will double fire, creating an infinite loop
		//so we need to wait a bit before setting the initialized flag
		//This is hopefully temporary until we can find a better solution.
		setTimeout(() => {
			initialized.current = true;
		}, 50);
	}, []);

	const openFileDialog = useCallback(
		(event: MouseEvent) => {
			event.preventDefault();
			const { disabled } = props;
			if (!disabled && fileInputRef.current !== null) {
				fileInputRef.current.click();
			}
		},
		[disabled],
	);

	const filesAdded = useCallback(
		(id: string, newFiles: SerializedFile[]) => {
			if (fileInputRef.current) fileInputRef.current.value = null;
			if (props.onFilesAdded) props.onFilesAdded(id, newFiles);
		},
		[props.onFilesAdded],
	);

	const addFiles = useCallback(
		async (filesToBeAdded: File[]) => {
			console.log("addFiles", filesToBeAdded);
			let newFiles: SerializedFile[];
			const convertedFiles = await convertFilesAsync(filesToBeAdded);
			if (multiple) newFiles = [...files, ...convertedFiles];
			else if (autoReplace || !files.length) {
				newFiles = convertedFiles;
			} else {
				return;
			}
			// if the files aren't being managed by the parent component and passed down to the file uploader
			// then save them in the state
			if (!Array.isArray(props.files)) {
				setFocused(false);
			} else {
				filesAdded(id, newFiles);
			}
			setFiles(newFiles);
		},
		[id, multiple, autoReplace, props.files, files, setFocused, filesAdded],
	);

	const onFilesAdded = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			if (event.target.files.length === 0) return;
			addFiles(fileListToArray(event.target.files));
		},
		[addFiles],
	);

	const onDragOver = useCallback(
		(event: DragEvent<HTMLDivElement>) => {
			event.preventDefault();
			if (!disabled && !focused) {
				setFocused(true);
			}
		},
		[disabled, focused, setFocused],
	);

	const onDragLeave = useCallback(
		(event: DragEvent<HTMLDivElement>) => {
			event.preventDefault();
			setFocused(false);
		},
		[setFocused],
	);

	const onDrop = useCallback(
		(event: DragEvent<HTMLDivElement>) => {
			event.preventDefault();
			if (!disabled) {
				addFiles(fileListToArray(event.dataTransfer.files));
			}
		},
		[disabled, addFiles],
	);

	const removeFile = useCallback(
		(event: MouseEvent<HTMLSpanElement>, index: number) => {
			event.preventDefault();
			event.stopPropagation();
			props.onFilesAdded(props.id, [...(files ?? props.files)], index);
		},
		[props.onFilesAdded, props.id, files],
	);

	useEffect(() => {
		//console.log("files", files);
	}, [files]);

	return (
		<FileUploadMarkup
			{...props}
			focused={focused}
			files={Array.isArray(props.files) ? props.files : files}
			openFileDialog={openFileDialog}
			removeFile={removeFile}
			onDragLeave={onDragLeave}
			addFiles={onFilesAdded}
			onDragOver={onDragOver}
			onDrop={onDrop}
			fileInputRef={fileInputRef}
		/>
	);
}

function convertFilesAsync(filesToBeConverted: File[]) {
	return Promise.all(
		filesToBeConverted.map(
			(file) =>
				new Promise<SerializedFile>((resolve) => {
					// SS: 5/19/26 - leaving the old serialized file for now as not all places will support the new form data upload. only implemented on rep documents
					if (file.size <= 1024 * 1024 * 20) {
						const reader = new FileReader();
						reader.onload = function () {
							resolve({
								name: file.name,
								type: file.type,
								url: reader.result as string,
								file,
							} as SerializedFile);
						};
						reader.readAsDataURL(file);
					} else {
						resolve({
							name: file.name,
							type: file.type,
							url: null,
							file,
						} as SerializedFile);
					}
				}),
		),
	);
}

function fileListToArray(fileList: FileList) {
	const files = [] as File[];
	for (let i = 0; i < fileList.length; i++) {
		files.push(fileList.item(i));
	}
	return files;
}
